/** @jsx jsx */
import { useContext } from "react"
import { useTransition, animated as a } from "react-spring"
import { Flex, Button, Text, jsx } from "theme-ui"

import Buttons from "../CaseBuilder/Buttons"
import { Exit, HowItWorks } from "../CaseBuilder/Icons"
import { SoldOut } from "../svg"

import { Context } from "../../context/MainContext"
import { ShopifyContext } from "../../context/ShopifyContext"

import enums from "../../util/enums"

function CaseBuilderModal({
  backgroundImage,
  colors,
  numItems,
  selectedVariants,
  flavors,
  bundleSize,
}) {
  const styles = {
    container: {
      color: colors.text,
      backgroundColor: "#EEA33B",
      overflow: "hidden",
      backgroundImage: `linear-gradient(rgba(238,163,59, 0.9) 0%, rgba(238,163,59, 0.9) 100%), url(${backgroundImage})`,
      backgroundSize: "cover",
      backgroundPosition: "50% 50%",
      backgroundRepeat: "no-repeat",
      fontSize: ["22px", "32px"],
      "@media screen and (max-width: 640px) and (min-height: 620px)": {
        fontSize: "28px",
      },
      position: "fixed",
      display: "grid",
      gridTemplateRows: "auto auto 1fr auto auto",
      gridTemplateColumns: "1fr",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 100,
      margin: "0px",
      padding: "24px 16px 18px",
      top: "0px",
      left: "0px",
      width: "100%",
      height: "100%",
      minHeight: "100vh",
      minHeight: "-webkit-fill-available",
      button: {
        fontSize: "1em",
        color: colors.text,
      },
      "&:before": {
        content: "''",
        position: "absolute",
        backgroundImage:
          "linear-gradient(180deg, rgba(239, 165, 62, 0) 0%, #EA9E36 100%)",
        bottom: "-90px",
        left: "0px",
        width: "100vw",
        height: "50%",
        pointerEvents: "none",
        zIndex: 1,
      },
    },
    header: {
      gridRow: 1,
      textAlign: "center",
      marginBottom: ["0.5em", "1em"],
      justifyContent: "center",
    },
    bundleSize: {
      gridRow: 2,
      justifyContent: "center",
      marginBottom: ["0.5em", "1em"],
    },
    flavors: {
      gridRow: 3,
      display: "flex",
      textAlign: "center",
      flexDirection: "column",
      justifyContent: "start",
      position: "relative",
      height: "100%",
      overflowY: "auto",
      overflowX: "hidden",
      ">div": {
        margin: "8px 0",
        flexShrink: 0,
      },
    },
    numItems: {
      gridRow: 4,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0.5em 0px",
    },
    footer: {
      gridRow: 5,
      zIndex: 1,
      marginTop: "10px",
      display: "grid",
      alignItems: "center",
      gridTemplateColumns: "auto 1fr auto",
    },
    mobileExitButton: {
      border: "none",
      padding: "0px",
      borderRadius: "50px",
      height: "40px",
      background: "transparent",
      boxShadow: "0 0 7px 0 rgba(0,0,0,0.50)",
    },
    addToCartButton: {
      justifySelf: "center",
      height: "55px",
      fontSize: "24px",
      "@media screen and (max-width: 640px) and (max-height: 548px)": {
        fontSize: "20px",
        height: "45px",
      },
      borderRadius: "100px",
      background: "white",
      padding: "0.25em 1em",
      boxShadow: "0 0 7px 0 rgba(0,0,0,0.50)",
      margin: "0px 0.5em",
      width: "90%",
      maxWidth: "270px",
    },
  }
  const {
    store: { caseBuilderMode, isMobile },
    setCaseBuilderMode,
    toggleHowItWorksOpen,
  } = useContext(Context)

  const {
    checkout,
    handleDecrement,
    handleIncrement,
    addBundleToCart,
  } = useContext(ShopifyContext)

  const transitions = useTransition(
    caseBuilderMode === enums.mode.bundle && isMobile,
    null,
    {
      from: { opacity: 0 },
      enter: { opacity: 1 },
      leave: { opacity: 0 },
      duration: 1000,
    }
  )

  function handleClose() {
    setCaseBuilderMode(enums.mode.view)
  }

  return transitions.map(
    ({ item, key, props }) =>
      item && (
        <a.div key={key} style={props} sx={styles.container} tabIndex="0">
          <Flex sx={styles.header}>
            <Text as="p" variant="body">
              {`Build a Case $60`}
            </Text>
          </Flex>
          <Flex sx={styles.bundleSize}>
            <Text as="p" variant="body">
              {"Choose"}
              &nbsp;
              <span
                sx={{ color: numItems > bundleSize ? "#E85A2E" : "inherit" }}
              >
                {numItems}
              </span>
              &nbsp;
              {"/"}
              &nbsp;
              {bundleSize}
              &nbsp;
              {" Pints"}
            </Text>
          </Flex>
          <Flex sx={styles.flavors}>
            {flavors.map(flavor => (
              <Flex
                key={flavor.title}
                sx={{ flexDirection: "column", alignItems: "center" }}
              >
                <Text
                  as="p"
                  sx={{
                    opacity: !selectedVariants.includes(flavor.productId)
                      ? "0.5"
                      : "1",
                  }}
                >
                  {!flavor.available ||
                    (flavor.disabled && (
                      <SoldOut style={{ marginRight: "0.25em" }} />
                    ))}
                  {flavor.title}
                </Text>
                <Buttons
                  productId={flavor.productId}
                  bundle={checkout.bundle}
                  numItems={numItems}
                  bundleSize={bundleSize}
                  disabled={!flavor.available || flavor.disabled}
                  increment={() => {
                    handleIncrement(
                      flavor[`1 OF ${bundleSize}PACK`],
                      flavor.productId,
                      checkout.bundleIndex
                    )
                  }}
                  decrement={() =>
                    handleDecrement(
                      flavor[`1 OF ${bundleSize}PACK`],
                      flavor.productId,
                      checkout.bundleIndex
                    )
                  }
                  color={
                    !flavor.available || flavor.disabled
                      ? "#E85A2E"
                      : colors.text
                  }
                />
              </Flex>
            ))}
          </Flex>
          <Flex sx={styles.footer}>
            <Button
              onClick={() => toggleHowItWorksOpen()}
              style={styles.mobileExitButton}
            >
              <HowItWorks />
            </Button>
            <Button
              onClick={async () => {
                if (numItems !== bundleSize) return
                const cart = await addBundleToCart(checkout.bundle)

                if (cart.errors === undefined) {
                  const timeout = setTimeout(() => {
                    setCaseBuilderMode(enums.mode.view)
                    clearTimeout(timeout)
                  }, 200)
                }
              }}
              sx={styles.addToCartButton}
            >
              {"Add to Cart"}
            </Button>
            <Button
              style={styles.mobileExitButton}
              onClick={() => handleClose()}
            >
              <Exit />
            </Button>
          </Flex>
        </a.div>
      )
  )
}

export default CaseBuilderModal
